<template>
  <div v-if="budget">
    <div class="page-header">
      <div>
        <div class="breadcrumb-sm">
          <span class="breadcrumb-link" @click="$router.back()">Project</span>
          <span class="breadcrumb-sep">›</span>
          <span>Budget v{{ budget.version }}</span>
        </div>
        <h5 class="page-title">Budget v{{ budget.version }} — {{ budget.type }}</h5>
        <p class="page-sub">{{ budget.project_id }}</p>
      </div>
      <div class="header-actions">
        <button v-if="budget.status === 'draft'" class="btn-outline" @click="submitBudget" :disabled="saving">Submit for Approval</button>
        <button v-if="budget.status === 'pending_approval'" class="btn-primary" @click="approveBudget" :disabled="saving">Approve Budget</button>
        <button v-if="budget.status === 'pending_approval'" class="btn-outline btn-outline--danger" @click="promptReject" :disabled="saving">Reject</button>
      </div>
    </div>

    <!-- Summary -->
    <div class="summary-grid">
      <div class="summary-card"><div class="summary-label">Labour</div><div class="summary-value">{{ formatMoney(budget.total_labour) }}</div></div>
      <div class="summary-card"><div class="summary-label">Materials</div><div class="summary-value">{{ formatMoney(budget.total_materials) }}</div></div>
      <div class="summary-card"><div class="summary-label">Expenses</div><div class="summary-value">{{ formatMoney(budget.total_expenses) }}</div></div>
      <div class="summary-card"><div class="summary-label">Contingency</div><div class="summary-value">{{ formatMoney(budget.total_contingency) }}</div></div>
      <div class="summary-card summary-card--total"><div class="summary-label">Grand Total</div><div class="summary-value">{{ formatMoney(budget.grand_total) }}</div></div>
    </div>

    <div class="card">
      <div class="card-header-row">
        <span class="card-section-title">Budget Lines</span>
        <span :class="['status-badge', `status-badge--${budget.status}`]">{{ budget.status }}</span>
      </div>
      <div v-if="!budget.lines?.length" class="empty-state-sm">No budget lines.</div>
      <table v-else class="data-table">
        <thead>
          <tr>
            <th>WBS Element</th>
            <th>Category</th>
            <th>Description</th>
            <th>Qty</th>
            <th>Unit</th>
            <th>Unit Cost</th>
            <th>Hours</th>
            <th>Total Cost</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="line in budget.lines" :key="line.id">
            <td class="mono">{{ line.wbs_element?.code || '—' }}</td>
            <td>{{ line.cost_category }}</td>
            <td>{{ line.description || '—' }}</td>
            <td>{{ line.qty || '—' }}</td>
            <td>{{ line.unit || '—' }}</td>
            <td class="amount-col">{{ line.unit_cost ? formatMoney(line.unit_cost) : '—' }}</td>
            <td>{{ line.hours || '—' }}</td>
            <td class="amount-col fw-600">{{ formatMoney(line.total_cost) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="7" class="text-right fw-600">Grand Total</td>
            <td class="amount-col fw-600">{{ formatMoney(budget.grand_total) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>

    <div v-if="budget.notes" class="card mt-12">
      <div class="card-section-title mb-8">Notes</div>
      <p>{{ budget.notes }}</p>
    </div>
  </div>
  <div v-else class="loading-state"><div class="spinner"></div><p>Loading budget...</p></div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';

export default {
  name: 'BudgetDetail',
  setup() { return { toast: useToast() }; },
  data() { return { budget: null, saving: false }; },
  async created() { await this.fetchBudget(); },
  methods: {
    async fetchBudget() {
      try { const { data } = await axios.get(`/projects/budgets/${this.$route.params.budgetId}`); this.budget = data.data; }
      catch { this.toast.error('Failed to load budget.'); }
    },
    async submitBudget() {
      this.saving = true;
      try { await axios.post(`/projects/budgets/${this.budget.id}/submit`); this.toast.success('Budget submitted.'); this.fetchBudget(); }
      catch (e) { this.toast.error(e.response?.data?.message ?? 'Error.'); }
      finally { this.saving = false; }
    },
    async approveBudget() {
      this.saving = true;
      try { await axios.post(`/projects/budgets/${this.budget.id}/approve`); this.toast.success('Budget approved.'); this.fetchBudget(); }
      catch (e) { this.toast.error(e.response?.data?.message ?? 'Error.'); }
      finally { this.saving = false; }
    },
    async promptReject() {
      const notes = prompt('Rejection notes:');
      this.saving = true;
      try { await axios.post(`/projects/budgets/${this.budget.id}/reject`, { notes }); this.toast.success('Budget rejected.'); this.fetchBudget(); }
      catch (e) { this.toast.error(e.response?.data?.message ?? 'Error.'); }
      finally { this.saving = false; }
    },
    formatMoney(v) { return new Intl.NumberFormat('en-NG', { minimumFractionDigits: 2 }).format(v ?? 0); },
    formatDate(d) { return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; },
  },
};
</script>

<style scoped>
/* ── Layout ─────────────────────────────────────────────────────────── */
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.header-actions { display:flex; align-items:center; gap:10px; }
.breadcrumb-sm   { display:flex; align-items:center; gap:6px; font-size:12px; color:#7a90b0; margin-bottom:4px; }
.breadcrumb-link { cursor:pointer; color:#004AAD; }
.breadcrumb-link:hover { text-decoration:underline; }
.breadcrumb-sep  { color:#c0cdd8; }
.mt-12 { margin-top:12px; }
.mb-8  { margin-bottom:8px; }
.text-right { text-align:right; }
.fw-600 { font-weight:600; }

/* ── Summary cards ──────────────────────────────────────────────────── */
.summary-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:20px; }
.summary-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:16px 20px; }
.summary-card--total { border-color:#004AAD; }
.summary-label { font-size:11.5px; font-weight:600; color:#7a90b0; text-transform:uppercase; letter-spacing:.04em; margin-bottom:6px; }
.summary-value { font-size:20px; font-weight:700; color:#1a2b45; }

/* ── Card / section ─────────────────────────────────────────────────── */
.card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.card.mt-12 { margin-top:12px; }
.card-header-row { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f4f8; }
.card-section-title { font-size:14px; font-weight:700; color:#1a2b45; }
.card-section-title.mb-8 { margin-bottom:8px; }
.empty-state-sm { padding:32px; text-align:center; color:#7a90b0; font-size:13px; }
.loading-state { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; color:#7a90b0; gap:10px; }

/* ── Data table ─────────────────────────────────────────────────────── */
.data-table { width:100%; border-collapse:collapse; }
.data-table th { padding:11px 16px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; text-align:left; white-space:nowrap; }
.data-table td { padding:13px 16px; font-size:13px; color:#1a2b45; border-bottom:1px solid #f0f4f8; vertical-align:middle; }
.data-table tbody tr:last-child td { border-bottom:none; }
.mono { font-family:monospace; font-size:12px; color:#004AAD; font-weight:600; }
.amount-col { text-align:right; font-variant-numeric:tabular-nums; }

/* ── Status badge ───────────────────────────────────────────────────── */
.status-badge { display:inline-block; font-size:11.5px; font-weight:600; border-radius:20px; padding:3px 10px; white-space:nowrap; }
.status-badge--draft     { background:#f1f5f9; color:#64748b; }
.status-badge--pending   { background:#eff6ff; color:#1d4ed8; }
.status-badge--approved  { background:#dcfce7; color:#15803d; }
.status-badge--rejected  { background:#fee2e2; color:#dc2626; }
.status-badge--baselined { background:#f0fdf4; color:#166534; }

/* ── Buttons ────────────────────────────────────────────────────────── */
.btn-primary { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary:hover:not(:disabled) { background:#003b8e; }
.btn-primary:disabled { opacity:.55; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; }
.btn-outline:hover { background:#f4f7fb; }
.btn-outline--danger { border-color:#fecaca; color:#dc2626; }
.btn-outline--danger:hover { background:#fee2e2; }

/* ── Spinner ────────────────────────────────────────────────────────── */
.spinner { width:32px; height:32px; border:3px solid #e4eaf2; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }

@media (max-width:768px) {
  .summary-grid { grid-template-columns:1fr 1fr; }
}
</style>
